import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../api'
import { getImageUrl } from '../config'

interface FormData {
  title: string
  slug: string
  thumbnail: string
  logo: string
  summary: string
  industry_category_id: string
  region_category_id: string
  hero_title: string
  hero_content: string
  content: string
  pdf_images: string
  pdf_download_url: string
  publish_date: string
  is_published: boolean
  sort_order: number
}

const emptyForm: FormData = {
  title: '', slug: '', thumbnail: '', logo: '', summary: '',
  industry_category_id: '', region_category_id: '',
  hero_title: '', hero_content: '', content: '', pdf_images: '', pdf_download_url: '',
  publish_date: '', is_published: false, sort_order: 0,
}

function parsePdfImages(value: string): string[] {
  if (!value) return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export default function CaseEditPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id
  const [form, setForm] = useState<FormData>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [r2Prefix, setR2Prefix] = useState('')
  const [generating, setGenerating] = useState(false)
  const [imagesUploading, setImagesUploading] = useState(false)
  const [imagesProgress, setImagesProgress] = useState({ current: 0, total: 0 })
  const [pdfUploading, setPdfUploading] = useState(false)
  const pdfInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isEdit) {
      api.get(`/api/admin/cases/${id}`).then((res) => {
        if (res.success && res.data) {
          const d = res.data
          setForm({
            title: d.title || '', slug: d.slug || '',
            thumbnail: d.thumbnail || '', logo: d.logo || '',
            summary: d.summary || '',
            industry_category_id: d.industry_category_id ? String(d.industry_category_id) : '',
            region_category_id: d.region_category_id ? String(d.region_category_id) : '',
            hero_title: d.hero_title || '', hero_content: d.hero_content || '',
            content: d.content || '',
            pdf_images: d.pdf_images || '',
            pdf_download_url: d.pdf_download_url || '',
            publish_date: d.publish_date || '',
            is_published: !!d.is_published,
            sort_order: d.sort_order || 0,
          })
        }
      })
    }
  }, [id])

  const update = (key: keyof FormData, value: string | boolean | number) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const handleUpload = async (file: File, field: 'thumbnail' | 'logo') => {
    setUploading(true)
    const formData = new FormData()
    formData.append('file', file)
    const res = await api.post('/api/admin/upload', formData)
    if (res.success && res.data?.url) {
      update(field, res.data.url)
    } else {
      alert(res.error || '上传失败')
    }
    setUploading(false)
  }

  // 上传 PDF 文件作为下载链接
  const handlePdfDownloadUpload = async (file: File) => {
    setPdfUploading(true)
    const formData = new FormData()
    formData.append('file', file)
    const res = await api.post('/api/admin/upload', formData)
    if (res.success && res.data?.url) {
      update('pdf_download_url', res.data.url)
    } else {
      alert(res.error || '上传失败')
    }
    setPdfUploading(false)
    if (pdfInputRef.current) pdfInputRef.current.value = ''
  }

  // 上传多张图片并追加到 pdf_images 数组
  const handlePdfImagesUpload = async (files: FileList) => {
    const existing = parsePdfImages(form.pdf_images).filter(u => u !== '__LOCK__')
    setImagesUploading(true)
    setImagesProgress({ current: 0, total: files.length })

    const newUrls: string[] = []
    for (let i = 0; i < files.length; i++) {
      setImagesProgress({ current: i + 1, total: files.length })
      const fd = new FormData()
      fd.append('file', files[i])
      try {
        const res = await api.post('/api/admin/upload', fd)
        if (res.success && res.data?.url) {
          newUrls.push(res.data.url)
        }
      } catch {
        // continue with next file
      }
    }

    const merged = [...existing, ...newUrls]
    update('pdf_images', JSON.stringify(merged, null, 2))
    setImagesUploading(false)
    setImagesProgress({ current: 0, total: 0 })
  }

  const handleRemovePdfImage = (index: number) => {
    const images = parsePdfImages(form.pdf_images)
    images.splice(index, 1)
    update('pdf_images', JSON.stringify(images, null, 2))
  }

  const handleR2Generate = async () => {
    if (!r2Prefix.trim()) {
      alert('请输入 R2 文件夹路径')
      return
    }

    setGenerating(true)
    try {
      const res = await api.get(`/api/admin/r2-list?prefix=${encodeURIComponent(r2Prefix.trim())}`)
      if (!res.success || !res.data?.length) {
        alert(res.error || '未找到文件')
        return
      }

      const files: { key: string; url: string }[] = res.data

      const collator = new Intl.Collator('zh', { numeric: true })
      const sorted = [...files].sort((a, b) => {
        const nameA = a.key.split('/').pop() || ''
        const nameB = b.key.split('/').pop() || ''
        return collator.compare(nameA, nameB)
      })

      const result: string[] = []
      for (const f of sorted) {
        result.push(f.url)
        const name = f.key.split('/').pop() || ''
        if (name.includes('隐藏')) {
          result.push('__LOCK__')
        }
      }

      update('pdf_images', JSON.stringify(result, null, 2))
    } catch (err: any) {
      alert('获取文件列表失败: ' + (err.message || err))
    } finally {
      setGenerating(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title.trim()) {
      alert('请输入标题')
      return
    }

    setSaving(true)
    const body = {
      ...form,
      industry_category_id: form.industry_category_id ? Number(form.industry_category_id) : null,
      region_category_id: form.region_category_id ? Number(form.region_category_id) : null,
      pdf_images: form.pdf_images || null,
    }

    const res = isEdit
      ? await api.put(`/api/admin/cases/${id}`, body)
      : await api.post('/api/admin/cases', body)

    if (res.success) {
      navigate('/cases')
    } else {
      alert(res.error || '保存失败')
    }
    setSaving(false)
  }

  const pdfImages = parsePdfImages(form.pdf_images)
  const lockIndex = pdfImages.indexOf('__LOCK__')
  const visibleImages = lockIndex === -1 ? pdfImages : pdfImages.slice(0, lockIndex)

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{isEdit ? '编辑案例' : '新增案例'}</h1>
      </div>

      <form onSubmit={handleSubmit} style={{ maxWidth: '720px' }}>
        <div style={{ background: 'white', padding: '24px', borderRadius: '8px', marginBottom: '16px' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '16px' }}>基本信息</h3>
          <div className="form-group">
            <label className="form-label">标题 *</label>
            <input className="form-input" value={form.title} onChange={(e) => update('title', e.target.value)} />
          </div>
        </div>

        <div style={{ background: 'white', padding: '24px', borderRadius: '8px', marginBottom: '16px' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '16px' }}>图片</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label">缩略图</label>
              {form.thumbnail && (
                <img src={getImageUrl(form.thumbnail)} alt="" style={{ width: '100%', maxHeight: '160px', objectFit: 'cover', borderRadius: '6px', marginBottom: '8px' }} />
              )}
              <input className="form-input" value={form.thumbnail} onChange={(e) => update('thumbnail', e.target.value)} placeholder="URL 或上传" />
              <input type="file" accept="image/*" style={{ marginTop: '8px' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f, 'thumbnail') }} />
            </div>
            <div className="form-group">
              <label className="form-label">Logo</label>
              {form.logo && (
                <img src={getImageUrl(form.logo)} alt="" style={{ width: '100%', maxHeight: '160px', objectFit: 'contain', borderRadius: '6px', marginBottom: '8px' }} />
              )}
              <input className="form-input" value={form.logo} onChange={(e) => update('logo', e.target.value)} placeholder="URL 或上传" />
              <input type="file" accept="image/*" style={{ marginTop: '8px' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f, 'logo') }} />
            </div>
          </div>
          {uploading && <p style={{ color: 'var(--color-primary)', fontSize: '13px' }}>上传中...</p>}
        </div>

        <div style={{ background: 'white', padding: '24px', borderRadius: '8px', marginBottom: '16px' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '16px' }}>正文内容</h3>

          {/* PDF 图片上传 + 拼接预览 */}
          <div className="form-group">
            <label className="form-label">PDF 图片</label>

            {/* 拼接预览 */}
            {visibleImages.length > 0 && (
              <div style={{
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                overflow: 'hidden',
                marginBottom: '12px',
              }}>
                <div style={{
                  padding: '8px 12px',
                  background: '#f9fafb',
                  borderBottom: '1px solid var(--color-border)',
                  fontSize: '13px',
                  color: 'var(--color-text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <span>拼接预览（{visibleImages.length} 页{lockIndex !== -1 ? `，锁定页起 ${pdfImages.length - visibleImages.length - 1} 张` : ''}）</span>
                </div>
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {visibleImages.map((url, i) => (
                    <div key={i} style={{ position: 'relative', borderBottom: i < visibleImages.length - 1 ? '2px solid #e5e7eb' : 'none' }}>
                      <img
                        src={getImageUrl(url)}
                        alt={`第 ${i + 1} 页`}
                        style={{ width: '100%', display: 'block' }}
                      />
                      <span style={{
                        position: 'absolute',
                        bottom: '4px',
                        right: '8px',
                        background: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                        fontSize: '11px',
                        padding: '1px 6px',
                        borderRadius: '3px',
                      }}>
                        {i + 1}/{visibleImages.length}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 缩略图列表 */}
            {visibleImages.length > 0 && (
              <div style={{
                display: 'flex',
                gap: '8px',
                overflowX: 'auto',
                paddingBottom: '8px',
                marginBottom: '12px',
              }}>
                {visibleImages.map((url, i) => (
                  <div key={i} style={{
                    position: 'relative',
                    flexShrink: 0,
                    width: '80px',
                    height: '110px',
                    border: '2px solid var(--color-border)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}>
                    <img
                      src={getImageUrl(url)}
                      alt={`缩略图 ${i + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <span style={{
                      position: 'absolute',
                      top: '2px',
                      left: '4px',
                      background: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      fontSize: '10px',
                      padding: '0 4px',
                      borderRadius: '2px',
                    }}>
                      {i + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemovePdfImage(i)}
                      style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        width: '18px',
                        height: '18px',
                        border: 'none',
                        borderRadius: '50%',
                        background: 'rgba(239,68,68,0.9)',
                        color: '#fff',
                        fontSize: '12px',
                        lineHeight: '18px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                      }}
                      title="移除"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* 上传按钮 */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
              <label style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 14px',
                background: 'var(--color-primary)',
                color: '#fff',
                borderRadius: '6px',
                fontSize: '13px',
                cursor: 'pointer',
              }}>
                {imagesUploading
                  ? `上传中 ${imagesProgress.current}/${imagesProgress.total}...`
                  : '📷 上传图片'}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={imagesUploading}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const files = e.target.files
                    if (files?.length) handlePdfImagesUpload(files)
                    e.target.value = ''
                  }}
                />
              </label>
            </div>

            {/* R2 生成 */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input
                className="form-input"
                value={r2Prefix}
                onChange={(e) => setR2Prefix(e.target.value)}
                placeholder="R2 文件夹路径，如 顺丰同城-WEBP"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="btn btn-outline"
                onClick={handleR2Generate}
                disabled={generating}
                style={{ whiteSpace: 'nowrap' }}
              >
                {generating ? '生成中...' : '从 R2 生成'}
              </button>
            </div>

            {/* JSON 编辑 */}
            <textarea
              className="form-input"
              style={{ fontFamily: 'monospace', fontSize: '12px', minHeight: '80px' }}
              value={form.pdf_images}
              onChange={(e) => update('pdf_images', e.target.value)}
              placeholder='["url1", "url2", "__LOCK__", ...]'
            />
          </div>

          {/* PDF 下载链接 */}
          <div className="form-group">
            <label className="form-label">PDF 下载链接</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                className="form-input"
                style={{ flex: 1 }}
                value={form.pdf_download_url}
                onChange={(e) => update('pdf_download_url', e.target.value)}
                placeholder="https://... 或上传 PDF 文件"
              />
              <label style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '0 14px',
                background: 'var(--color-primary)',
                color: '#fff',
                borderRadius: '6px',
                fontSize: '13px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                height: '44px',
              }}>
                {pdfUploading ? '上传中...' : '上传 PDF'}
                <input
                  ref={pdfInputRef}
                  type="file"
                  accept=".pdf"
                  disabled={pdfUploading}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) handlePdfDownloadUpload(f)
                  }}
                />
              </label>
            </div>
          </div>
        </div>

        <div style={{ background: 'white', padding: '24px', borderRadius: '8px', marginBottom: '16px' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '16px' }}>发布设置</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label">发布日期</label>
              <input className="form-input" type="date" value={form.publish_date} onChange={(e) => update('publish_date', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">排序</label>
              <input className="form-input" type="number" value={form.sort_order} onChange={(e) => update('sort_order', Number(e.target.value))} />
            </div>
            <div className="form-group">
              <label className="form-label">状态</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                <input type="checkbox" checked={form.is_published} onChange={(e) => update('is_published', e.target.checked)} />
                已发布
              </label>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </button>
          <button type="button" className="btn btn-outline" onClick={() => navigate('/cases')}>取消</button>
        </div>
      </form>
    </div>
  )
}
